/**
 * ProviderTile — single provider button used in the "Connect a provider"
 * modal. Click fires a Request Access toast (provider integrations are not
 * yet live). Shared so the modal and any future provider-catalog views
 * render the exact same tile.
 */

import { Send } from 'lucide-react';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { fireProviderRequestAccessToast } from './providerRequestAccess';
import { type ProviderEntry } from './requestAccessConfig';
import { type DataMethod } from './types';

/** First 2 uppercase chars of a name — monogram fallback when a provider has no logo. */
const computeMonogram = (name: string): string => {
  const cleaned = name.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  if (!cleaned) {
    return '?';
  }
  const parts = cleaned.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
};

interface ProviderTileProps {
  provider: ProviderEntry;
  /** DataMethod recorded with the Request Access toast (telemetry). */
  method: DataMethod;
}

export const ProviderTile = ({ provider, method }: ProviderTileProps) => (
  <button
    type='button'
    onClick={() => fireProviderRequestAccessToast({ providerLabel: provider.name, method })}
    className={cn(
      'tw:group tw:relative tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-xl tw:border tw:bg-white tw:text-center tw:transition-all tw:cursor-pointer',
      'tw:border-[#e5e7eb] tw:hover:border-emerald-400 tw:hover:-translate-y-[1px] tw:p-4',
    )}
  >
    <div
      className={cn(
        'tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-xl',
        provider.logoUrl ? 'tw:w-full' : provider.iconBg,
      )}
      style={{ height: 44, ...(provider.logoUrl ? {} : { width: 44 }) }}
    >
      {provider.logoUrl ? (
        <img
          src={provider.logoUrl}
          alt={`${provider.name} logo`}
          className='tw:max-h-8 tw:max-w-[85%] tw:object-contain'
        />
      ) : (
        <span className={cn('tw:font-bold', provider.iconColor)} style={{ fontSize: 16, letterSpacing: -1 }}>
          {computeMonogram(provider.name)}
        </span>
      )}
    </div>
    <div className='tw:text-[11.5px] tw:font-semibold tw:text-[#1a1d1f] tw:leading-[1.25]'>{provider.name}</div>
    {provider.note && (
      <div className='tw:text-[10.5px] tw:text-muted-foreground tw:leading-[1.25]'>{provider.note}</div>
    )}
    <div className='tw:absolute tw:top-2 tw:right-2 tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-emerald-600 tw:text-white tw:px-2 tw:py-0.5 tw:text-[10.5px] tw:font-semibold tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity'>
      <Send className='tw:size-3' />
      Request Access
    </div>
  </button>
);
